import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { pagePath } from '../lib/pages'
import { betroffeneVersionen } from '../lib/advisory'
import {
  describeMetric,
  formatAgo,
  formatDateTime,
  formatMetricValue,
  plural,
  registerMetricInfo,
} from '../lib/format'
import { useAuth } from '../lib/auth'
import type {
  AccessSession,
  Advisory,
  ConfigBackup,
  Customer,
  Device,
  DeviceEvent,
  DeviceMetrics,
  GuiSession,
  Sample,
  DeviceTemplate,
} from '../lib/types'
import { LiveKarten } from '../components/LiveKarten'
import { ZustandKarte, zustandsZeilen } from '../components/ZustandKarte'
import { EigeneAbfragen } from '../components/EigeneAbfragen'
import { SyslogKarte } from '../components/SyslogKarte'
import { DatenverkehrKarte } from '../components/DatenverkehrKarte'
import { PortTunnel } from '../components/PortTunnel'
import { WlanKarte } from '../components/WlanKarte'
import { GeraetAenderungenKarte, GeraetDiensteKarte } from '../components/CmdbKarten'
import { VerlaufAbschnitt } from './VerlaufAbschnitt'
import { Sparkline } from '../components/Sparkline'
import { liveHinweis, useLiveModus, useMitschnitt } from '../lib/live'
import { useAenderungen } from '../lib/stream'
import { DeviceStateBadge, StatusBadge } from '../components/Status'
import { ErrorBox, Loading } from '../components/Feedback'
import { VerbindungsDialog } from '../components/VerbindungsDialog'
import { SshFenster } from '../components/SshFenster'
import { reviewLabel } from './Configs'
import { KonfigurationsHinweis } from '../components/KonfigurationsHinweis'
import { GeraetVerwaltung, hasCredential } from './GeraetVerwaltung'

interface Props {
  deviceId: string
  customerSlug: string | null
}

// Kennzahlen, die oben als Kacheln stehen können. Gezeigt wird nur, was das
// Template dieses Geräts auch erhebt: "Sitzungen" ergibt bei einer Firewall
// Sinn, bei einem Linux-Host nicht.
const KPI_METRICS = [
  'cpu_percent',
  'memory_percent',
  'swap_percent',
  'disk_percent',
  'session_count',
  'icmp_rtt_ms',
]

// Reihenfolge für die Vorauswahl im Verlauf: die erste Metrik, zu der es
// Werte gibt, wird gezeigt. Ohne das stand dort CPU, auch wenn das Gerät gar
// keine CPU-Werte liefert.
const HISTORY_PREFERENCE = [
  'cpu_percent',
  'memory_percent',
  'if_in_bits_per_s',
  'if_out_bits_per_s',
  'disk_percent',
  'icmp_rtt_ms',
]

/** Die Durchsatz-Metriken; sie hängen an je einem Port. */
const THROUGHPUT = ['if_in_bits_per_s', 'if_out_bits_per_s']

// Was als Verlauf nichts hergibt: Zählerstände seit dem Start des Geräts sind
// eine stetig steigende Gerade, ein Portstatus ist eine Stufe zwischen zwei
// Codes. Beides sagt nichts über die Last — derselbe Fund wie in der
// Interface-Tabelle. Sie kommen nur dran, wenn es sonst nichts gibt.
const POOR_HISTORY = [
  'if_in_octets',
  'if_out_octets',
  'if_in_errors',
  'if_out_errors',
  'if_oper_status',
]

/** Was der Verlauf zeigt: eine Metrik, bei Tabellen-Metriken dazu ein Port. */
export interface HistoryChoice {
  metric: string
  port?: string
}

/**
 * Der Port mit dem meisten Verkehr, und in welcher Richtung. Virtuelle Ports
 * und abgeschaltete bleiben außen vor: der Verlauf soll den Anschluss zeigen,
 * über den das Gerät wirklich redet.
 */
export function busiestInterface(samples: Sample[]): HistoryChoice | null {
  const up = new Set<string>()
  for (const s of samples) {
    if (s.metric === 'if_oper_status' && s.value === 1 && s.labels?.ifname) {
      up.add(s.labels.ifname)
    }
  }
  let best: HistoryChoice | null = null
  let bestValue = -1
  for (const s of samples) {
    if (!THROUGHPUT.includes(s.metric)) continue
    const port = s.labels?.ifname
    if (!port || isVirtualPort(port)) continue
    // Ohne gemeldeten Status wird nicht gefiltert, sonst bliebe die Auswahl
    // leer, solange nur die Zähler da sind.
    if (up.size > 0 && !up.has(port)) continue
    if (s.value > bestValue) {
      bestValue = s.value
      best = { metric: s.metric, port }
    }
  }
  return best
}

/**
 * Was der Verlauf ohne Zutun zeigt. Vorher wurde die erste Metrik mit Werten
 * genommen — alphabetisch, und damit bei einem Gerät ohne CPU-Werte
 * „Fehler eingehend", eine Linie, die konstant auf null liegt. Jetzt zählt
 * erst die Vorzugsliste, beim Durchsatz der wichtigste aktive Port, und zum
 * Schluss lieber eine Metrik, die sich überhaupt bewegt.
 */
export function defaultHistorySelection(samples: Sample[]): HistoryChoice {
  for (const candidate of HISTORY_PREFERENCE) {
    if (THROUGHPUT.includes(candidate)) {
      const busiest = busiestInterface(samples)
      if (busiest) return busiest
      continue
    }
    if (samples.some((s) => s.metric === candidate)) return { metric: candidate }
  }
  const metrics = uniqueMetrics(samples)
  const moved = (m: string) => samples.some((s) => s.metric === m && s.value !== 0)
  const moving = metrics.find((m) => !POOR_HISTORY.includes(m) && moved(m))
  return { metric: moving ?? metrics.find(moved) ?? metrics[0] ?? 'icmp_rtt_ms' }
}

export function GeraetDetailPage({ deviceId, customerSlug }: Props) {
  const [chosen] = useState<HistoryChoice | null>(null)
  const [showVirtual, setShowVirtual] = useState(false)
  // Fernzugriff: erst der Dialog, dann die Sitzung.
  const [ask, setAsk] = useState<'ssh' | 'gui' | null>(null)
  const [terminal, setTerminal] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  // Ohne zugeordneten SSH-Zugang wird kein Fehler geworfen, sondern der
  // Weg zur Zuordnung gezeigt.
  const [sshHint, setSshHint] = useState(false)
  const [tunnelOffen, setTunnelOffen] = useState(false)
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const live = useLiveModus(deviceId, can('live.start'))

  // Punkt 1: die Seite frischt sich ohne Neuladen auf. Der Datenstrom sagt
  // nur, dass es etwas Neues gibt; geholt wird es über die üblichen Abfragen.
  useAenderungen(() => {
    void queryClient.invalidateQueries({ queryKey: ['device-metrics', deviceId] })
    void queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
    void queryClient.invalidateQueries({ queryKey: ['live-history', deviceId] })
  }, deviceId)

  const device = useQuery({
    queryKey: ['device', deviceId],
    queryFn: ({ signal }) => apiGet<Device>(`/devices/${deviceId}`, signal),
  })


  const metrics = useQuery({
    queryKey: ['device-metrics', deviceId],
    queryFn: ({ signal }) => apiGet<DeviceMetrics>(`/devices/${deviceId}/metrics`, signal),
    // Im Live-Modus fragt der Agent alle fünf Sekunden; dann soll die Seite
    // nicht dreißig Sekunden auf dem alten Wert sitzen bleiben.
    refetchInterval: live.aktiv ? live.intervalS * 1000 : 30_000,
  })

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    staleTime: 60_000,
  })

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => apiGet<DeviceTemplate[]>('/templates', signal),
    staleTime: 300_000,
  })

  const accesses = useQuery({
    queryKey: ['access-sessions', deviceId],
    queryFn: ({ signal }) =>
      apiGet<AccessSession[]>(`/access-sessions?geraet=${deviceId}`, signal),
    refetchInterval: 60_000,
  })

  const configs = useQuery({
    queryKey: ['device-configs', deviceId],
    queryFn: ({ signal }) => apiGet<ConfigBackup[]>(`/devices/${deviceId}/configs`, signal),
    enabled: can('config.view'),
  })

  const runBackup = useMutation({
    mutationFn: () => apiSend<ConfigBackup>('POST', `/devices/${deviceId}/backup`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['device-configs', deviceId] })
      void queryClient.invalidateQueries({ queryKey: ['configs'] })
    },
  })

  const advisories = useQuery({
    queryKey: ['device-advisories', deviceId],
    queryFn: ({ signal }) => apiGet<Advisory[]>(`/devices/${deviceId}/advisories`, signal),
  })

  const events = useQuery({
    queryKey: ['device-events', deviceId],
    queryFn: ({ signal }) => apiGet<DeviceEvent[]>(`/devices/${deviceId}/ereignisse?anzahl=20`, signal),
    refetchInterval: 60_000,
  })

  // Vorauswahl nach Datenlage: der Verlauf zeigt eine Metrik, zu der es
  // wirklich Werte gibt — beim Durchsatz den wichtigsten aktiven Port.
  const samples = metrics.data?.latest ?? []
  const measured = uniqueMetrics(samples)
  const gewaehlt = chosen ?? (measured.length > 0 ? defaultHistorySelection(samples) : { metric: '' })
  // Die Ports, zu denen es bei dieser Metrik Werte gibt. Leer heißt: die
  // Metrik hängt am Gerät, nicht an einem Port.
  const ports = portsFor(samples, gewaehlt.metric)
  const port = ports.length > 0 ? (ports.includes(gewaehlt.port ?? '') ? gewaehlt.port! : ports[0]) : ''
  const selection: HistoryChoice = port ? { metric: gewaehlt.metric, port } : { metric: gewaehlt.metric }

  // Mini-Verlauf je Port für die Interface-Tabelle. Er wird aus der
  // Momentaufnahme mitgeschrieben, die die Seite ohnehin holt — zwanzig
  // eigene Abfragen alle fünf Sekunden wären der falsche Weg.
  const durchsatzJetzt: Record<string, number> = {}
  for (const row of groupInterfaces(samples)) {
    if (row.inBits !== undefined) durchsatzJetzt[row.name || row.index] = row.inBits
  }
  const durchsatzVerlauf = useMitschnitt(durchsatzJetzt, samples[0]?.time)

  if (device.isError) return <ErrorBox error={device.error} what="Das Gerät" />
  if (device.isPending) return <Loading what="Gerätedaten" />

  const d = device.data
  const color = customers.data?.find((c) => c.slug === d.customer_slug)?.color
  const latest = metrics.data?.latest ?? []
  const scalars = latest.filter((s) => !s.labels || Object.keys(s.labels).length === 0)
  const allInterfaces = groupInterfaces(latest)
  const interfaces = showVirtual ? allInterfaces : allInterfaces.filter((row) => !isVirtualPort(row.name))
  const virtualCount = allInterfaces.length - allInterfaces.filter((row) => !isVirtualPort(row.name)).length
  const filesystems = groupFilesystems(latest)
  // Zählerstände da, Rate nicht: entweder fehlt noch die zweite Abfrage oder
  // der Agent ist zu alt. Ein dauerhaftes "wird gemessen …" ohne Erklärung
  // war der Fund aus dem Betrieb.
  const waitingForRate =
    interfaces.length > 0 &&
    interfaces.some((row) => row.inOctets !== undefined && row.inBits === undefined)
  const reachable = metrics.data?.checks.find((c) => c.check_key === 'reachable')
  const template = templates.data?.find((t) => t.key === d.template_key)
  // Eine Karte erscheint, wenn das Template die Kennzahl erhebt oder schon
  // ein Wert da ist.
  const templateMetrics = new Set(template?.metrics ?? [])
  const kpis = KPI_METRICS.filter(
    (key) => templateMetrics.has(key) || scalars.some((s) => s.metric === key),
  )
  // Name und Einheit der Kennzahlen, die nur das Template kennt: der Verlauf
  // soll "Restlaufzeit" schreiben, nicht "ups_runtime_minutes".
  registerMetricInfo(template?.metric_info ?? [])
  const zustand = zustandsZeilen(template?.metric_info ?? [], latest, new Set(kpis))

  return (
    <>
      <KonfigurationsHinweis
        lage={{
          fehler: d.agent_config_error,
          uebersprungen: d.agent_skipped,
          agentName: d.agent_name,
          agentVersion: d.agent_version,
          befehl: d.agent_update_command,
        }}
      />
      <div className="page-head">
        <div>
          <h1 className="page-title">{d.name}</h1>
          <div className="page-sub">
            <span className="mono">{d.mgmt_ip}</span> · {d.customer_name} · {d.site_name} ·{' '}
            Template {d.template_key}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <DeviceStateBadge state={d.state} />
          {/* Punkt 2: solange jemand hinsieht, fragt der Agent dieses eine
              Gerät alle fünf Sekunden ab. */}
          {can('live.start') ? (
            live.aktiv ? (
              <>
                <span className="live-abzeichen" role="status">
                  <span className="live-punkt" aria-hidden="true" />
                  {liveHinweis(live.intervalS, live.verbleibend)}
                </span>
                <button type="button" className="btn" onClick={live.beenden}>
                  Live beenden
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn"
                disabled={live.startet || d.agent_id === undefined}
                onClick={live.start}
              >
                {live.startet ? 'Startet …' : 'Live-Modus'}
              </button>
            )
          ) : null}
          {can('remote.gui') ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setConnectError(null)
                setAsk('gui')
              }}
            >
              Web-GUI öffnen
            </button>
          ) : null}
          {can('remote.ssh') ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setConnectError(null)
                if (!hasCredential(d, 'ssh')) {
                  setSshHint(true)
                  return
                }
                setSshHint(false)
                setAsk('ssh')
              }}
            >
              SSH-Terminal
            </button>
          ) : null}
          {can('remote.tunnel') ? (
            <button type="button" className="btn" onClick={() => setTunnelOffen((o) => !o)}>
              Port-Tunnel
            </button>
          ) : null}
          {can('config.view') ? (
            <button
              type="button"
              className="btn"
              disabled={runBackup.isPending}
              onClick={() => runBackup.mutate()}
            >
              {runBackup.isPending ? 'Sichere …' : 'Config jetzt sichern'}
            </button>
          ) : null}
          <Link to={pagePath('geraete', customerSlug ?? d.customer_slug ?? null)} className="btn">
            Zur Liste
          </Link>
        </div>
      </div>

      {tunnelOffen ? <PortTunnel deviceId={deviceId} onClose={() => setTunnelOffen(false)} /> : null}

      {sshHint ? (
        <div className="hinweis" style={{ background: 'var(--warn-bg)' }} role="status">
          <strong style={{ color: 'var(--warn)' }}>Kein SSH-Zugang hinterlegt</strong>
          <span>
            Diesem Gerät ist kein Zugang aus dem Tresor für SSH zugeordnet. Ohne ihn kommt keine
            Sitzung zustande.
          </span>
          <a href="#zugangsdaten" onClick={() => setSshHint(false)}>
            Zugang zuordnen
          </a>
        </div>
      ) : null}

      {ask ? (
        <VerbindungsDialog
          device={d}
          kind={ask}
          color={color}
          busy={connecting}
          error={connectError}
          onClose={() => setAsk(null)}
          onConfirm={() => {
            if (ask === 'ssh') {
              setAsk(null)
              setTerminal(true)
              return
            }
            setConnecting(true)
            setConnectError(null)
            apiSend<GuiSession>('POST', `/devices/${d.id}/gui`)
              .then((session) => {
                setAsk(null)
                // Eigenes Fenster: die Sitzung lebt auf ihrer eigenen
                // Subdomain und darf nichts von der Netzwarte mitnehmen.
                window.open(session.url, '_blank', 'noopener,noreferrer')
                void accesses.refetch()
              })
              .catch((error: unknown) => {
                setConnectError(error instanceof Error ? error.message : 'Die Sitzung kam nicht zustande.')
              })
              .finally(() => setConnecting(false))
          }}
        />
      ) : null}

      {terminal ? (
        <SshFenster
          device={d}
          color={color}
          onClose={() => {
            setTerminal(false)
            void accesses.refetch()
          }}
        />
      ) : null}

      {live.fehler ? (
        <div className="hinweis" style={{ background: 'var(--warn-bg)' }} role="alert">
          <strong style={{ color: 'var(--warn)' }}>Live-Modus nicht gestartet</strong>
          <span>{live.fehler}</span>
        </div>
      ) : null}

      {/* Punkt 3 und 4: das Ende von selbst und die belegten Plätze. */}
      {live.aktiv ? (
        <div className="hinweis" style={{ background: 'var(--acc-soft)' }} role="status">
          <strong style={{ color: 'var(--acc)' }}>Live-Modus</strong>
          <span>
            {d.agent_name || 'Der Agent'} fragt {d.name} alle {live.intervalS} Sekunden ab.
            Endet von selbst nach 10 Minuten ohne Aktivität oder beim Verlassen der Seite.
            Belegt: {live.belegt} von {live.grenze} Live-Plätzen
            {d.agent_name ? ` auf ${d.agent_name}` : ''}.
          </span>
        </div>
      ) : null}

      {kpis.length > 0 ? (
        <div className="kpi-grid">
          {kpis.map((key) => {
            const sample = scalars.find((s) => s.metric === key)
            const { label } = describeMetric(key)
            return (
              <div className="kpi" key={key}>
                <span className="kpi-label">{label}</span>
                <span className="kpi-value">
                  {sample ? formatMetricValue(key, sample.value) : '–'}
                </span>
                <span className="kpi-sub">
                  {sample ? formatAgo(sample.time) : 'noch nicht abgefragt'}
                </span>
              </div>
            )
          })}
        </div>
      ) : null}

      <ZustandKarte zeilen={zustand} />

      <EigeneAbfragen deviceId={deviceId} latest={latest} darfVerwalten={can('device.manage')} />

      {/* Punkt 5: die drei Live-Graphen. Sie erscheinen nur im Live-Modus —
          mit einem Messwert je Minute hätten sie nichts zu zeigen. */}
      {live.aktiv ? (
        <LiveKarten
          deviceId={deviceId}
          latest={latest}
          intervalS={live.intervalS}
          port={busiestInterface(latest)?.port ?? ''}
        />
      ) : null}

      {/* Punkte 6 bis 12: Zeiträume, Zoom, mehrere Messgrößen, Markierungen
          und die Detailreiter. */}
      <VerlaufAbschnitt
        deviceId={deviceId}
        latest={latest}
        measured={measured}
        portsFor={(m) => portsFor(latest, m)}
        vorauswahl={selection}
        kerne={coresOf(latest)}
        // Dieselbe Auswahl wie in der Tabelle: virtuelle Ports sind auch im
        // Detailreiter Rauschen, und der Entwurf zählt sie nicht mit.
        interfaces={interfaces}
        dateisysteme={filesystems}
      />

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Interfaces</h2>
          <div className="row" style={{ gap: 10 }}>
            {virtualCount > 0 ? (
              <button
                type="button"
                className="btn btn-sm"
                aria-pressed={showVirtual}
                onClick={() => setShowVirtual((v) => !v)}
              >
                {showVirtual
                  ? 'Virtuelle ausblenden'
                  : `Virtuelle einblenden (${virtualCount})`}
              </button>
            ) : null}
            <span className="muted" style={{ fontSize: 12 }}>
              {plural(interfaces.length, 'Port', 'Ports')}
            </span>
          </div>
        </div>
        {interfaces.length === 0 ? (
          <div className="empty">
            <span>Noch keine Interface-Werte abgefragt.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Port</th>
                <th>Status</th>
                {/* Was zählt, ist der Durchsatz jetzt; der Zählerstand seit
                    dem Start des Geräts steht klein darunter. */}
                <th>Eingehend</th>
                <th>Ausgehend</th>
                <th>letzte 5 min</th>
                <th>Fehler ein</th>
                <th>Fehler aus</th>
              </tr>
            </thead>
            <tbody>
              {interfaces.map((row) => (
                <tr key={row.index}>
                  <td className="mono">{row.name || row.index}</td>
                  <td>
                    {row.operStatus === undefined ? (
                      <StatusBadge state="off" label="Unbekannt" />
                    ) : row.operStatus === 1 ? (
                      <StatusBadge state="ok" label="Aktiv" />
                    ) : (
                      <StatusBadge state="off" label="Inaktiv" />
                    )}
                  </td>
                  <td className="mono">
                    {row.inBits === undefined ? (
                      <span className="muted">wird gemessen …</span>
                    ) : (
                      formatMetricValue('if_in_bits_per_s', row.inBits)
                    )}
                    {row.inOctets === undefined ? null : (
                      <div className="muted" style={{ fontSize: 12 }}>
                        gesamt {fmt(row.inOctets, 'if_in_octets')}
                      </div>
                    )}
                  </td>
                  <td className="mono">
                    {row.outBits === undefined ? (
                      <span className="muted">wird gemessen …</span>
                    ) : (
                      formatMetricValue('if_out_bits_per_s', row.outBits)
                    )}
                    {row.outOctets === undefined ? null : (
                      <div className="muted" style={{ fontSize: 12 }}>
                        gesamt {fmt(row.outOctets, 'if_out_octets')}
                      </div>
                    )}
                  </td>
                  <td>
                    {/* Punkt 5: der Mini-Verlauf neben der Zahl. Er füllt
                        sich mit jeder Abfrage; im Live-Modus also alle fünf
                        Sekunden. */}
                    {(durchsatzVerlauf[row.name || row.index]?.length ?? 0) > 1 ? (
                      <Sparkline
                        points={durchsatzVerlauf[row.name || row.index] ?? []}
                        label={`Durchsatz ${row.name || row.index}, letzte Messpunkte`}
                        color="var(--ok)"
                      />
                    ) : (
                      <span className="muted" style={{ fontSize: 12 }}>
                        wird aufgezeichnet …
                      </span>
                    )}
                  </td>
                  <td className="mono">{fmt(row.inErrors, 'if_in_errors')}</td>
                  <td className="mono">{fmt(row.outErrors, 'if_out_errors')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {waitingForRate ? (
          <div className="card-body muted" style={{ fontSize: 12, paddingTop: 0 }}>
            Der Durchsatz wird aus zwei Zählerständen gerechnet und steht nach der
            zweiten Abfrage — bei {d.poll_interval_s} s Intervall also nach rund{' '}
            {2 * d.poll_interval_s} s. Bleibt er dauerhaft aus, ist der Agent älter
            als diese Zentrale: gerechnet wird die Rate im Agent, nicht hier.{' '}
            {d.agent_name || 'Der Agent'} meldet Fassung{' '}
            <span className="mono">{d.agent_version || 'unbekannt'}</span>. Agents
            werden über ihr eigenes Installationsskript aktualisiert, nicht über das
            Update der Zentrale (docs/installation.md).
          </div>
        ) : null}
      </section>

      {filesystems.length > 0 ? (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Dateisysteme</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              {plural(filesystems.length, 'Einhängepunkt', 'Einhängepunkte')}
            </span>
          </div>
          <table className="data">
            <thead>
              <tr>
                <th>Einhängepunkt</th>
                <th>Art</th>
                <th>Belegt</th>
              </tr>
            </thead>
            <tbody>
              {filesystems.map((fs) => (
                <tr key={fs.mount}>
                  <td className="mono">{fs.mount}</td>
                  <td>
                    {fs.network ? (
                      <StatusBadge state="off" label="Netzlaufwerk" />
                    ) : (
                      <span className="muted">lokal</span>
                    )}
                  </td>
                  <td className="mono">
                    {formatMetricValue('filesystem_percent', fs.percent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Firmware &amp; Sicherheit</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Hinweise aus den Einstellungen und der KEV-Liste der CISA
          </span>
        </div>
        <div className="card-body stack" style={{ gap: 10 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="muted">Installiert</span>
            <span className="mono">{d.firmware_version || 'unbekannt'}</span>
          </div>
          {(advisories.data?.length ?? 0) === 0 ? (
            <span className="muted" style={{ fontSize: 13 }}>
              Zu dieser Version ist kein Hinweis hinterlegt.
            </span>
          ) : (
            (advisories.data ?? []).map((a) => (
              <div
                key={a.id}
                className="hinweis"
                style={{ background: a.schwere === 'crit' ? 'var(--crit-bg)' : 'var(--warn-bg)' }}
              >
                <strong style={{ color: a.schwere === 'crit' ? 'var(--crit)' : 'var(--warn)' }}>
                  {a.cve ? `${a.cve}: ` : ''}
                  {a.titel}
                </strong>
                {a.ausgenutzt ? <span>Wird laut CISA aktiv ausgenutzt.</span> : null}
                <span>Betrifft {betroffeneVersionen(a)}.</span>
                {a.url ? (
                  <a href={a.url} target="_blank" rel="noreferrer noopener">
                    Advisory öffnen
                  </a>
                ) : null}
              </div>
            ))
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Meldungen des Geräts</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            SNMP-Traps und erkannte Syslog-Ereignisse
          </span>
        </div>
        {(events.data?.length ?? 0) === 0 ? (
          <div className="empty">
            <span>
              Noch nichts gemeldet. Traps und Syslog werden am Agent angenommen, wenn sie für den
              Standort eingeschaltet sind.
            </span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Art</th>
                <th>Meldung</th>
              </tr>
            </thead>
            <tbody>
              {(events.data ?? []).map((e) => (
                <tr key={e.id}>
                  <td>{formatDateTime(e.time)}</td>
                  <td>
                    <span
                      className={`badge ${
                        e.severity === 'crit'
                          ? 'badge-crit'
                          : e.severity === 'warn'
                            ? 'badge-warn'
                            : 'badge-info'
                      }`}
                    >
                      {e.kind}
                    </span>
                  </td>
                  <td>{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {can('config.view') ? (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Konfiguration</h2>
            <Link to={pagePath('configs', customerSlug ?? d.customer_slug ?? null)}>
              Alle Versionen
            </Link>
          </div>
          {runBackup.isError ? (
            <div className="card-body">
              <ErrorBox error={runBackup.error} what="Die Sicherung" />
            </div>
          ) : null}
          {(configs.data?.length ?? 0) === 0 ? (
            <div className="empty">
              <span>
                Noch keine Sicherung. Sie braucht einen SSH-Zugang im Tresor und ein Template mit
                Sicherungsbefehl.
              </span>
            </div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Auslöser</th>
                  <th>Änderung</th>
                  <th>Prüfstatus</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(configs.data ?? []).slice(0, 5).map((b) => (
                  <tr key={b.id}>
                    <td>{formatDateTime(b.taken_at)}</td>
                    <td>{b.trigger === 'syslog' ? 'Admin-Login' : b.trigger === 'manuell' ? 'von Hand' : 'Zeitplan'}</td>
                    <td>
                      {b.error ? (
                        <span className="badge badge-crit">Fehlgeschlagen</span>
                      ) : b.changed ? (
                        <span className="mono">
                          +{b.added} −{b.removed}
                        </span>
                      ) : (
                        <span className="muted">unverändert</span>
                      )}
                    </td>
                    <td>{reviewLabel(b)}</td>
                    <td>
                      <a href={`/api/configs/${b.id}/inhalt`}>Herunterladen</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Letzte Zugriffe</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Zugangsdaten liegen im Tresor; Techniker sehen das Kennwort nicht
          </span>
        </div>
        {accesses.isPending ? (
          <div className="card-body">
            <Loading what="Zugriffe" />
          </div>
        ) : (accesses.data?.length ?? 0) === 0 ? (
          <div className="empty">
            <span>Auf dieses Gerät hat noch niemand über die Netzwarte zugegriffen.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Wer</th>
                <th>Wie</th>
                <th>Beginn</th>
                <th>Dauer</th>
                <th>Aufzeichnung</th>
              </tr>
            </thead>
            <tbody>
              {(accesses.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td>{a.user_name || a.user_email || 'unbekannt'}</td>
                  <td>{a.kind === 'ssh' ? 'SSH' : a.kind === 'gui' ? 'Web-GUI' : 'Weiterleitung'}</td>
                  <td>{formatDateTime(a.started_at)}</td>
                  <td>{sessionDuration(a)}</td>
                  <td>
                    {a.recording_ref ? (
                      <a href={`/api/access-sessions/${a.id}/recording`}>Aufzeichnung</a>
                    ) : (
                      <span className="muted">–</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Stammdaten</h2>
        </div>
        <table className="data">
          <tbody>
            <Row label="Hersteller">{d.vendor || '–'}</Row>
            <Row label="Modell">{d.model || '–'}</Row>
            <Row label="Firmware">{d.firmware_version || '–'}</Row>
            <Row label="Seriennummer">{d.serial || '–'}</Row>
            <Row label="sysObjectID">
              <span className="mono">{d.sys_object_id || '–'}</span>
            </Row>
            <Row label="Systemtext">
              <span className="mono">{d.sys_descr || '–'}</span>
            </Row>
            <Row label="Zuständiger Agent">
              {d.agent_name || 'keiner'}
              {d.home_agent_id ? (
                <div style={{ color: 'var(--warn)', fontSize: 12 }}>
                  vertritt {d.home_agent_name || 'den eigentlichen Agent'}, der ausgefallen ist
                </div>
              ) : null}
            </Row>
            <Row label="SNMP-Port">
              <span className="mono">{d.snmp_port}</span>
            </Row>
            <Row label="Web-GUI">
              <span className="mono">
                {d.gui_scheme}://{d.mgmt_ip}:{d.gui_port}
              </span>
            </Row>
            <Row label="SSH">
              <span className="mono">
                {d.mgmt_ip}:{d.ssh_port}
              </span>
            </Row>
            <Row label="Abfrageintervall">{d.poll_interval_s} s</Row>
            <Row label="Erreichbarkeit seit">
              {reachable ? formatDateTime(reachable.since) : '–'}
            </Row>
            <Row label="Angelegt">{formatDateTime(d.created_at)}</Row>
          </tbody>
        </table>
      </section>

      <WlanKarte deviceId={deviceId} customerSlug={customerSlug ?? d.customer_slug ?? null} />
      <DatenverkehrKarte
        deviceId={deviceId}
        aufbewahrungTage={customers.data?.find((c) => c.slug === d.customer_slug)?.flow_retention_days ?? 0}
      />
      <SyslogKarte deviceId={deviceId} customerSlug={customerSlug ?? d.customer_slug ?? null} />
      <GeraetDiensteKarte deviceId={deviceId} />
      <GeraetAenderungenKarte deviceId={deviceId} />

      <GeraetVerwaltung device={d} templates={templates.data ?? []} />
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr>
      <th style={{ width: 220 }}>{label}</th>
      <td>{children}</td>
    </tr>
  )
}

/** Laufzeit einer Sitzung, oder "läuft" solange sie offen ist. */
function sessionDuration(a: AccessSession): string {
  if (!a.ended_at) return 'läuft'
  const ms = Date.parse(a.ended_at) - Date.parse(a.started_at)
  if (!Number.isFinite(ms) || ms < 0) return '–'
  const minutes = Math.round(ms / 60000)
  return minutes < 1 ? 'unter 1 min' : `${minutes} min`
}

function fmt(value: number | undefined, metric: string): string {
  return value === undefined ? '–' : formatMetricValue(metric, value)
}

/** Eine Messreihe aus den Verlaufspunkten. */

/**
 * Die Metriken, zu denen dieses Gerät wirklich Werte geliefert hat. Früher
 * standen hier CPU und Latenz immer mit drin; bei einem Gerät ohne
 * CPU-Werte führte das zu einem leeren Verlauf.
 */
function uniqueMetrics(samples: Sample[]): string[] {
  const seen = new Set<string>()
  for (const s of samples) seen.add(s.metric)
  return [...seen].sort()
}

/**
 * Die Ports, zu denen eine Metrik Werte hat. Eine leere Liste heißt: die
 * Metrik hängt am Gerät selbst und braucht keine Portauswahl.
 */
export function portsFor(samples: Sample[], metric: string): string[] {
  const seen = new Set<string>()
  for (const s of samples) {
    if (s.metric !== metric) continue
    if (s.labels?.ifname) seen.add(s.labels.ifname)
  }
  return [...seen].sort((a, b) => a.localeCompare(b, 'de', { numeric: true }))
}

/**
 * Virtuelle Ports eines Virtualisierungshosts: je Gast ein veth, dazu
 * Bridges und tap-Geräte. Sie verdecken sonst die echten Anschlüsse.
 */
/** Die einzelnen Kerne aus der Momentaufnahme; im Live-Modus sind es Zeilen. */
export function coresOf(samples: Sample[]): { index: string }[] {
  const out: { index: string }[] = []
  for (const s of samples) {
    const index = s.labels?.index
    if (s.metric !== 'cpu_percent' || !index) continue
    out.push({ index })
  }
  return out.sort((a, b) => Number(a.index) - Number(b.index))
}

export function isVirtualPort(name: string | undefined): boolean {
  if (!name) return false
  return /^(veth|fwbr|fwln|fwpr|tap|vnet|docker|br-|virbr|lo$)/.test(name)
}

interface FilesystemRow {
  mount: string
  percent: number
  network: boolean
}

/** Dateisysteme aus den Messwerten, Netzlaufwerke gekennzeichnet. */
export function groupFilesystems(samples: Sample[]): FilesystemRow[] {
  const out: FilesystemRow[] = []
  for (const s of samples) {
    if (s.metric !== 'filesystem_percent' || !s.labels?.mount) continue
    out.push({ mount: s.labels.mount, percent: s.value, network: s.labels.art === 'netz' })
  }
  return out.sort((a, b) => a.mount.localeCompare(b.mount))
}

interface InterfaceRow {
  index: string
  name?: string
  operStatus?: number
  inOctets?: number
  outOctets?: number
  inBits?: number
  outBits?: number
  inErrors?: number
  outErrors?: number
}

/** Interface-Messwerte nach Port-Index zusammenfassen. */
/** Die Metriken, die zu einem Port gehören. */
const INTERFACE_METRICS = new Set([
  'if_oper_status',
  'if_in_octets',
  'if_out_octets',
  'if_in_bits_per_s',
  'if_out_bits_per_s',
  'if_in_errors',
  'if_out_errors',
])

export function groupInterfaces(samples: Sample[]): InterfaceRow[] {
  const byIndex = new Map<string, InterfaceRow>()
  for (const s of samples) {
    const index = s.labels?.index
    // Nur Port-Metriken. Im Live-Modus trägt auch jeder CPU-Kern ein
    // "index"-Etikett; ohne diese Schranke stünden sechzehn namenlose
    // Zeilen in der Interface-Tabelle.
    if (!index || !INTERFACE_METRICS.has(s.metric)) continue
    const row = byIndex.get(index) ?? { index }
    if (s.labels?.ifname) row.name = s.labels.ifname
    switch (s.metric) {
      case 'if_oper_status':
        row.operStatus = s.value
        break
      case 'if_in_octets':
        row.inOctets = s.value
        break
      case 'if_out_octets':
        row.outOctets = s.value
        break
      case 'if_in_bits_per_s':
        row.inBits = s.value
        break
      case 'if_out_bits_per_s':
        row.outBits = s.value
        break
      case 'if_in_errors':
        row.inErrors = s.value
        break
      case 'if_out_errors':
        row.outErrors = s.value
        break
    }
    byIndex.set(index, row)
  }
  return [...byIndex.values()].sort((a, b) => Number(a.index) - Number(b.index))
}

/** Tabellenansicht zum Diagramm, damit die Werte nicht allein an der Linie hängen. */
